import { useState } from 'react';

/**
 * Hook for managing table row expansion state
 *
 * This hook provides functionality for expanding and collapsing table rows,
 * typically used for hierarchical data or detail views within rows.
 *
 * @param initialState - Initial expansion state as record of rowId to boolean
 * @param onRowExpand - Optional callback when a row is expanded/collapsed
 *
 * @returns Object containing:
 *   - expanded: Current expansion state for all rows
 *   - onRowExpand: Function to toggle expansion of a specific row
 *   - setExpanded: Function to set the entire expansion state
 *
 * @example
 * ```tsx
 * const { expanded, onRowExpand, setExpanded } = useExpansion(
 *   { 'row-1': true, 'row-2': false }, // Initially expanded state
 *   (rowId, isExpanded) => console.log(`Row ${rowId} is ${isExpanded ? 'expanded' : 'collapsed'}`)
 * );
 *
 * // Toggle row expansion
 * onRowExpand('row-3');
 *
 * // Check if row is expanded
 * const isRowExpanded = expanded['row-1'];
 *
 * // Expand all rows
 * setExpanded({ 'row-1': true, 'row-2': true, 'row-3': true });
 * ```
 */
export const useExpansion = (
  initialState: Record<string | number, boolean> = {},
  onRowExpand?: (rowId: string | number, isExpanded: boolean) => void,
) => {
  const [expanded, setExpanded] = useState(initialState);

  const handleRowExpand = (rowId: string | number) => {
    const isExpanded = !expanded[rowId];
    setExpanded((prev) => ({ ...prev, [rowId]: isExpanded }));
    onRowExpand?.(rowId, isExpanded);
  };

  return { expanded, onRowExpand: handleRowExpand, setExpanded };
};
